import React from "react";
import styled from "styled-components";
import { css } from "styled-components/macro"; // eslint-disable-line
import { motion } from "framer-motion";
import { FileText, Mail, Phone } from "lucide-react";
import LandingLayout from "layouts/LandingLayout";
import Hero, { SlantedBackground } from "components/hero/BackgroundAsImage";
import useInView from "helpers/useInView";
import { fadeUpVariants } from "lib/animations";
import config from "lib/config";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C_PRIMARY = "#ff6c00";
const C_SECONDARY = "#111111";

// ─── Animation ───────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MDiv = motion.div as any;

const FadeUpSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
}> = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({ once: true });
  return (
    <MDiv
      ref={ref as React.Ref<HTMLDivElement>}
      variants={fadeUpVariants}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
    >
      {children}
    </MDiv>
  );
};

// ─── Styled ──────────────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 5rem 1.5rem 6rem;
`;

const DocCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  padding: 3rem;

  @media (max-width: 640px) {
    padding: 1.75rem;
  }
`;

const DocHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
`;

const DocHeaderIcon = styled.div`
  width: 3rem;
  height: 3rem;
  background: #fff4ec;
  border-left: 3px solid ${C_PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${C_PRIMARY};
  flex-shrink: 0;
`;

const DocMeta = styled.div``;

const DocTitle = styled.h1`
  font-size: 1.25rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin-bottom: 0.2rem;
`;

const DocDate = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  font-weight: 900;
  color: ${C_SECONDARY};
  margin: 2rem 0 0.75rem;
  padding-left: 0.75rem;
  border-left: 3px solid ${C_PRIMARY};
`;

const Paragraph = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.85;
  margin-bottom: 0.85rem;
`;

const BulletList = styled.ul`
  margin: 0.5rem 0 1rem 1.25rem;
  list-style: disc;
`;

const BulletItem = styled.li`
  font-size: 0.9rem;
  color: #4b5563;
  line-height: 1.85;
  margin-bottom: 0.3rem;
`;

const PriceTable = styled.div`
  border: 1px solid #e5e7eb;
  margin: 1.25rem 0;
  overflow: hidden;
`;

const PriceRow = styled.div<{ $header?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  background: ${(p) => (p.$header ? C_SECONDARY : "white")};
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:nth-child(even):not(:first-child) {
    background: #fafafa;
  }
`;

const PriceCell = styled.div<{ $header?: boolean }>`
  padding: 0.75rem 1.25rem;
  font-size: ${(p) => (p.$header ? "0.68rem" : "0.85rem")};
  font-weight: ${(p) => (p.$header ? "900" : "600")};
  color: ${(p) => (p.$header ? "white" : "#374151")};
  text-transform: ${(p) => (p.$header ? "uppercase" : "none")};
  letter-spacing: ${(p) => (p.$header ? "0.1em" : "normal")};
  border-right: 1px solid #e5e7eb;

  &:last-child {
    border-right: none;
  }
`;

const PriceCellAccent = styled(PriceCell)`
  color: ${C_PRIMARY};
  font-weight: 900;
`;

const InfoBox = styled.div<{ $variant?: "warning" | "info" }>`
  padding: 1.25rem 1.5rem;
  border-left: 3px solid
    ${(p) => (p.$variant === "warning" ? "#f59e0b" : C_PRIMARY)};
  background: ${(p) => (p.$variant === "warning" ? "#fffbeb" : "#fff4ec")};
  margin: 1.25rem 0;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.75;
`;

const ContactBox = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ContactBoxTitle = styled.p`
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #9ca3af;
  margin-bottom: 0.25rem;
`;

const ContactLine = styled.p`
  font-size: 0.875rem;
  color: ${C_SECONDARY};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const CgvPage: React.FC = () => (
  <LandingLayout disableAnimation>
    <Hero
      backgroundCss={`linear-gradient(135deg, ${C_SECONDARY} 0%, #1a1a1a 100%)`}
      notification="CGV"
      heading={
        <>
          <span>Conditions générales</span>
          <br />
          <SlantedBackground>de vente.</SlantedBackground>
        </>
      }
      description="Conditions applicables à l'ensemble des prestations proposées par MomsEduc Study."
    />

    <PageWrapper>
      <FadeUpSection>
        <DocCard>
          <DocHeader>
            <DocHeaderIcon>
              <FileText size={22} />
            </DocHeaderIcon>
            <DocMeta>
              <DocTitle>Conditions Générales de Vente</DocTitle>
              <DocDate>En vigueur depuis le 1er janvier 2026</DocDate>
            </DocMeta>
          </DocHeader>

          {/* ── Article 1 ── */}
          <SectionTitle>Article 1 — Objet et champ d'application</SectionTitle>
          <Paragraph>
            Les présentes Conditions Générales de Vente (CGV) régissent
            l'ensemble des relations contractuelles entre {config.appName}{" "}
            {config.subAppName} (ci-après « le Prestataire ») et toute personne
            physique utilisant ses services (ci-après « le Client »).
          </Paragraph>
          <Paragraph>
            Toute commande implique l'acceptation pleine et entière des
            présentes CGV. Le Client reconnaît avoir pris connaissance des CGV
            avant de valider sa commande.
          </Paragraph>

          {/* ── Article 2 ── */}
          <SectionTitle>Article 2 — Services proposés</SectionTitle>
          <Paragraph>Le Prestataire propose notamment :</Paragraph>
          <BulletList>
            <BulletItem>
              L'établissement d'une Attestation de Virement Irrévocable (AVI)
              destinée aux demandes de visa et inscriptions universitaires.
            </BulletItem>
            <BulletItem>
              L'accompagnement dans la constitution du dossier visa étudiant.
            </BulletItem>
            <BulletItem>
              La recherche et réservation de logement étudiant.
            </BulletItem>
            <BulletItem>
              L'assistance à la recherche de billets d'avion.
            </BulletItem>
            <BulletItem>
              Le support administratif et financier tout au long du parcours.
            </BulletItem>
          </BulletList>

          {/* ── Article 3 — Tarifs ── */}
          <SectionTitle>Article 3 — Tarifs</SectionTitle>
          <Paragraph>
            Les tarifs en vigueur sont indiqués TTC en euros. Ils sont
            susceptibles d'évoluer, mais le tarif applicable est celui en
            vigueur au moment de la validation de la commande.
          </Paragraph>
          <PriceTable>
            <PriceRow $header>
              <PriceCell $header>Prestation</PriceCell>
              <PriceCell $header>Tarif</PriceCell>
              <PriceCell $header>Délai estimé</PriceCell>
            </PriceRow>
            <PriceRow>
              <PriceCell>Attestation AVI</PriceCell>
              <PriceCellAccent>75 €</PriceCellAccent>
              <PriceCell>48h ouvrées</PriceCell>
            </PriceRow>
            <PriceRow>
              <PriceCell>Accompagnement Visa</PriceCell>
              <PriceCellAccent>120 €</PriceCellAccent>
              <PriceCell>Selon consulat</PriceCell>
            </PriceRow>
            <PriceRow>
              <PriceCell>Logement Étudiant</PriceCell>
              <PriceCellAccent>150 €</PriceCellAccent>
              <PriceCell>5–10 jours</PriceCell>
            </PriceRow>
            <PriceRow>
              <PriceCell>Billets d'Avion</PriceCell>
              <PriceCellAccent>50 €</PriceCellAccent>
              <PriceCell>24h</PriceCell>
            </PriceRow>
            <PriceRow>
              <PriceCell>Support 24/7</PriceCell>
              <PriceCellAccent>30 €/mois</PriceCellAccent>
              <PriceCell>Continu</PriceCell>
            </PriceRow>
          </PriceTable>

          {/* ── Article 4 ── */}
          <SectionTitle>Article 4 — Commande et confirmation</SectionTitle>
          <Paragraph>
            La commande est réputée ferme et définitive après : (1) remplissage
            du formulaire de demande, (2) paiement intégral des frais de
            service, (3) réception d'un e-mail de confirmation de notre part.
          </Paragraph>
          <InfoBox>
            <strong>Important :</strong> Le délai de livraison court à partir de
            la réception de l'ensemble des documents requis et du paiement
            confirmé, et non de la date de la commande initiale.
          </InfoBox>

          {/* ── Article 5 ── */}
          <SectionTitle>Article 5 — Paiement</SectionTitle>
          <Paragraph>
            Le paiement est exigible intégralement à la commande. Les moyens de
            paiement acceptés sont : virement bancaire, Mobile Money (Orange
            Money, MTN Mobile Money), et carte bancaire via notre passerelle
            sécurisée.
          </Paragraph>
          <Paragraph>
            Aucune prestation ne sera démarrée avant confirmation du paiement.
          </Paragraph>

          {/* ── Article 6 ── */}
          <SectionTitle>Article 6 — Délais de livraison</SectionTitle>
          <Paragraph>
            Le Prestataire s'engage à respecter les délais indiqués dans le
            tableau de tarification, sous réserve de la fourniture complète des
            pièces justificatives par le Client.
          </Paragraph>
          <InfoBox $variant="warning">
            En cas de dépassement du délai garanti imputable uniquement au
            Prestataire, le Client peut demander un remboursement total de la
            prestation AVI.
          </InfoBox>

          {/* ── Article 7 ── */}
          <SectionTitle>Article 7 — Rétractation et remboursement</SectionTitle>
          <Paragraph>
            Compte tenu de la nature des services (prestation intellectuelle et
            administrative commencée sur demande expresse), le droit de
            rétractation de 14 jours prévu par le Code de la consommation ne
            s'applique pas une fois la prestation démarrée.
          </Paragraph>
          <BulletList>
            <BulletItem>
              Avant démarrage de la prestation : remboursement intégral sous 5
              jours ouvrés.
            </BulletItem>
            <BulletItem>
              Après démarrage : aucun remboursement, sauf retard imputable au
              Prestataire (voir Article 6).
            </BulletItem>
          </BulletList>

          {/* ── Article 8 ── */}
          <SectionTitle>Article 8 — Obligations du Client</SectionTitle>
          <Paragraph>Le Client s'engage à :</Paragraph>
          <BulletList>
            <BulletItem>
              Fournir des informations exactes et complètes lors de la commande.
            </BulletItem>
            <BulletItem>
              Transmettre l'ensemble des pièces justificatives dans les délais
              convenus.
            </BulletItem>
            <BulletItem>
              Ne pas utiliser les documents fournis à des fins frauduleuses.
            </BulletItem>
          </BulletList>

          {/* ── Article 9 ── */}
          <SectionTitle>Article 9 — Responsabilité</SectionTitle>
          <Paragraph>
            Le Prestataire est tenu à une obligation de moyens. Sa
            responsabilité ne saurait être engagée en cas de refus de visa
            décidé souverainement par les autorités consulaires, ni pour des
            délais indépendants de sa volonté (grèves, cas de force majeure).
          </Paragraph>

          {/* ── Article 10 ── */}
          <SectionTitle>Article 10 — Propriété intellectuelle</SectionTitle>
          <Paragraph>
            Les documents produits par le Prestataire (attestations, modèles,
            guides) sont protégés par le droit d'auteur. Le Client dispose d'un
            droit d'usage personnel et non transmissible sur les documents
            commandés.
          </Paragraph>

          {/* ── Article 11 ── */}
          <SectionTitle>Article 11 — Litiges et droit applicable</SectionTitle>
          <Paragraph>
            Les présentes CGV sont soumises au droit français. En cas de litige,
            les parties s'engagent à rechercher une solution amiable avant toute
            action judiciaire. À défaut d'accord, les tribunaux compétents du
            ressort du siège social du Prestataire seront saisis.
          </Paragraph>

          {/* ── Contact ── */}
          <SectionTitle>Article 12 — Contact</SectionTitle>
          <Paragraph>
            Pour toute question relative aux présentes CGV ou à une commande :
          </Paragraph>
          <ContactBox>
            <ContactBoxTitle>
              Service client {config.appName} {config.subAppName}
            </ContactBoxTitle>
            <ContactLine>
              <Mail size={15} color={C_PRIMARY} />
              {config.contactEmail}
            </ContactLine>
            <ContactLine>
              <Phone size={15} color={C_PRIMARY} />
              {config.phonesFrance} — France · Lun–Sam 8h–22h
            </ContactLine>
            <ContactLine>
              <Phone size={15} color={C_PRIMARY} />
              {config.phonesCameroun} — Cameroun · Lun–Sam 8h–22h
            </ContactLine>
          </ContactBox>
        </DocCard>
      </FadeUpSection>
    </PageWrapper>
  </LandingLayout>
);

export default CgvPage;
